import { useState } from 'react';
import type {
  OnboardingAnswers,
  OnboardingField,
  PhaseId,
  PhaseStatus,
} from '@wedpo/soc2';
import {
  STAKEHOLDER_ORDER,
  isOnboardingFieldFilled,
  kickoff,
  onboardingProgress,
  onboardingTemplate,
  preworkChecklist,
  roleShortLabels,
} from '@wedpo/soc2';
import type { ViewId } from '@/lib/store';
import { KickoffDeck } from '@/views/KickoffDeck';

type Track = 'onboarding' | 'slides' | 'prework';

interface TrackCard {
  key: Track;
  tag: string;
  title: string;
  desc: string;
}

const CARDS: TrackCard[] = [
  {
    key: 'onboarding',
    tag: 'Form',
    title: 'Onboarding',
    desc: 'Profile your company, systems, team, security posture, and the documents you already have.',
  },
  {
    key: 'slides',
    tag: 'Slides',
    title: 'Kickoff deck',
    desc: 'A guided deck that frames the engagement, the timeline, and who owns what - presented by Osh.',
  },
  {
    key: 'prework',
    tag: 'Checklist',
    title: 'Preliminary actions',
    desc: 'A pre-work checklist plus a scoped, time-boxed access grant for your auditor.',
  },
];

export function Phase1({
  answers,
  setAnswers,
  prework,
  togglePrework,
  phaseStatus,
  p1Done,
  go,
}: {
  answers: OnboardingAnswers;
  setAnswers: (updater: (prev: OnboardingAnswers) => OnboardingAnswers) => void;
  prework: string[];
  togglePrework: (id: string) => void;
  phaseStatus: Record<PhaseId, PhaseStatus>;
  p1Done: boolean;
  go: (v: ViewId) => void;
}) {
  const [track, setTrack] = useState<Track>('slides');

  const op = onboardingProgress(answers);
  const checked = new Set(prework);

  const cardMeta: Record<Track, string> = {
    onboarding: `${op.filled} / ${op.total} answered`,
    slides: `${kickoff.deck.length} slides`,
    prework: `${checked.size} / ${preworkChecklist.length} done`,
  };

  return (
    <section>
      <p className="eyebrow">Phase 1 · onboarding &amp; kickoff</p>
      <h1 className="title">Onboarding &amp; kickoff</h1>
      <p className="sub">
        Three tracks run in parallel before gap analysis opens: get the deck in front
        of the team, profile your environment, and clear the pre-work.
      </p>

      {p1Done ? (
        <div className="unlock-banner">
          <span className="ub-tick">✓</span>
          <div style={{ flex: 1 }}>
            <strong>Phase 1 complete.</strong> Gap analysis is unlocked.
          </div>
          <button className="btn btn-grad" onClick={() => go('p2')}>
            Go to gap analysis →
          </button>
        </div>
      ) : (
        <div className="unlock-hint">
          To unlock Phase 2: complete the onboarding form ({op.filled}/{op.total}{' '}
          required) and the pre-work checklist ({checked.size}/{preworkChecklist.length}).
        </div>
      )}

      <Kickoff phaseStatus={phaseStatus} />

      <div className="track-cards">
        {CARDS.map((c) => (
          <button
            key={c.key}
            className={`track-card${track === c.key ? ' on' : ''}`}
            onClick={() => setTrack(c.key)}
          >
            <span className="tc-head">
              <span className="tag">{c.tag}</span>
              <span className="tc-meta">{cardMeta[c.key]}</span>
            </span>
            <h3>{c.title}</h3>
            <p>{c.desc}</p>
          </button>
        ))}
      </div>

      <div className="track-body">
        {track === 'slides' && <KickoffDeck phaseStatus={phaseStatus} />}
        {track === 'onboarding' && (
          <Onboarding answers={answers} setAnswers={setAnswers} />
        )}
        {track === 'prework' && (
          <Prework
            answers={answers}
            checked={checked}
            togglePrework={togglePrework}
          />
        )}
      </div>
    </section>
  );
}

/* ---------- Animated kickoff timeline ---------- */

function Kickoff({
  phaseStatus,
}: {
  phaseStatus: Record<PhaseId, PhaseStatus>;
}) {
  return (
    <div className="ko-overview">
      <p className="ko-intro">{kickoff.intro}</p>
      <div className="ko-wrap">
        <div className="ko-track" aria-hidden>
          <span className="ko-fill" />
        </div>
        <div className="ko-row">
          {kickoff.phases.map((ph, i) => {
            const st = phaseStatus[ph.id];
            return (
              <div
                className="ko-seg"
                key={ph.id}
                style={{ flexGrow: ph.weeks, animationDelay: `${i * 0.16}s` }}
              >
                <div className={`ko-node ko-${st}`}>{ph.n}</div>
                <div className="ko-label">{ph.label}</div>
                <div className="ko-lead">
                  <span className={`owdot ow-${ph.lead}`} />
                  {roleShortLabels[ph.lead]}
                </div>
                <div className="ko-blurb">{ph.blurb}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------- Onboarding form ---------- */

function Onboarding({
  answers,
  setAnswers,
}: {
  answers: OnboardingAnswers;
  setAnswers: (updater: (prev: OnboardingAnswers) => OnboardingAnswers) => void;
}) {
  const op = onboardingProgress(answers);
  const setVal = (id: string, v: string | string[]) =>
    setAnswers((prev) => ({ ...prev, [id]: v }));

  const toggleMulti = (id: string, option: string) =>
    setAnswers((prev) => {
      const cur = Array.isArray(prev[id]) ? (prev[id] as string[]) : [];
      const next = cur.includes(option)
        ? cur.filter((x) => x !== option)
        : [...cur, option];
      return { ...prev, [id]: next };
    });

  return (
    <div>
      <div className="subtabs" style={{ border: 'none', marginBottom: '.75rem' }}>
        <div style={{ flex: 1 }} />
        <span
          className="mono"
          style={{ fontSize: 11, color: 'var(--ink-60)', letterSpacing: '.08em' }}
        >
          {op.filled} / {op.total} required answered
        </span>
      </div>
      <div className="bar" style={{ margin: '0 0 1.5rem' }}>
        <span style={{ width: `${op.pct}%` }} />
      </div>

      {onboardingTemplate.groups.map((g) => (
        <div key={g.id} className="qgroup">
          <div className="gh">
            <span className="gt">{g.title}</span>
          </div>
          {g.intro && (
            <p
              style={{
                color: 'var(--ink-60)',
                fontSize: '.84rem',
                margin: '0 0 1rem',
                lineHeight: 1.5,
              }}
            >
              {g.intro}
            </p>
          )}
          {g.fields.map((f) => (
            <Field
              key={f.id}
              field={f}
              answers={answers}
              setVal={setVal}
              toggleMulti={toggleMulti}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function Field({
  field: f,
  answers,
  setVal,
  toggleMulti,
}: {
  field: OnboardingField;
  answers: OnboardingAnswers;
  setVal: (id: string, v: string | string[]) => void;
  toggleMulti: (id: string, option: string) => void;
}) {
  const filled = isOnboardingFieldFilled(f, answers);
  const strVal = (answers[f.id] as string) || '';
  const arrVal = Array.isArray(answers[f.id]) ? (answers[f.id] as string[]) : [];

  return (
    <div className="qfield">
      <div className="fl">
        {f.label}
        {f.required && <span className="req">required</span>}
        {f.required && filled && <span className="okdot" aria-label="answered">✓</span>}
      </div>
      {f.guidance && <div className="fg">{f.guidance}</div>}
      {f.feeds && <span className="hintchip policy">feeds: {f.feeds}</span>}

      {f.type === 'text' && (
        <input
          value={strVal}
          placeholder={f.placeholder || 'Type your answer...'}
          onChange={(e) => setVal(f.id, e.target.value)}
        />
      )}

      {f.type === 'textarea' && (
        <textarea
          value={strVal}
          placeholder={f.placeholder || 'Type your answer...'}
          onChange={(e) => setVal(f.id, e.target.value)}
        />
      )}

      {f.type === 'choice' && (
        <select value={strVal} onChange={(e) => setVal(f.id, e.target.value)}>
          <option value="">Choose…</option>
          {(f.options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      )}

      {f.type === 'multi' && (
        <div className="chipset">
          {(f.options || []).map((o) => {
            const on = arrVal.includes(o);
            return (
              <button
                key={o}
                type="button"
                className={`chiptog${on ? ' on' : ''}`}
                onClick={() => toggleMulti(f.id, o)}
              >
                {o}
              </button>
            );
          })}
        </div>
      )}

      {f.type === 'list' && (
        <textarea
          value={arrVal.join('\n')}
          placeholder={f.placeholder || 'One per line...'}
          onChange={(e) => setVal(f.id, e.target.value.split('\n'))}
        />
      )}

      {f.type === 'raci' && (
        <div className="raci">
          {STAKEHOLDER_ORDER.map((role) => {
            const key = `${f.id}.${role}`;
            return (
              <div key={role} className="raci-row">
                <span className="raci-role">
                  <span className={`owdot ow-${role}`} />
                  {roleShortLabels[role]}
                </span>
                <input
                  value={(answers[key] as string) || ''}
                  placeholder="Name <email>"
                  onChange={(e) => setVal(key, e.target.value)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------- Pre-work checklist + auditor access ---------- */

function Prework({
  answers,
  checked,
  togglePrework,
}: {
  answers: OnboardingAnswers;
  checked: Set<string>;
  togglePrework: (id: string) => void;
}) {
  const accessGranted = checked.has('grant-access');
  const auditorEmail = ((answers['auditContact'] as string) || '').trim();

  return (
    <div>
      <div className="card auditor-card">
        <div style={{ flex: 1 }}>
          <span className="tag">Auditor access</span>
          <h3 style={{ margin: '.3rem 0 .15rem' }}>Scoped, time-boxed grant</h3>
          <p>
            Read-only access to the evidence repository and system description,
            limited to the observation window plus 30 days.
          </p>
          <div className="acc-meta">
            <span>
              <em>Auditor</em> {auditorEmail || 'set in onboarding form'}
            </span>
            <span>
              <em>Scope</em> Evidence + system description (read-only)
            </span>
            <span>
              <em>Window</em> 01 Jul 2026 - 31 Jan 2027
            </span>
          </div>
        </div>
        <div className="acc-action">
          <span className={`pst pst-${accessGranted ? 'adopted' : 'not-started'}`}>
            {accessGranted ? 'Provisioned' : 'Not provisioned'}
          </span>
          <button
            className={`btn ${accessGranted ? 'btn-ghost' : 'btn-grad'}`}
            onClick={() => togglePrework('grant-access')}
          >
            {accessGranted ? 'Revoke' : 'Grant access'}
          </button>
        </div>
      </div>

      <h2 className="sec">
        Pre-work checklist{' '}
        <span className="recur">
          {checked.size} / {preworkChecklist.length} done
        </span>
      </h2>
      <div className="checklist">
        {preworkChecklist.map((item) => {
          const on = checked.has(item.id);
          return (
            <button
              key={item.id}
              type="button"
              className={`clk-row${on ? ' done' : ''}`}
              onClick={() => togglePrework(item.id)}
            >
              <span className={`cl-box${on ? ' on' : ''}`}>{on ? '✓' : ''}</span>
              <span style={{ flex: 1 }}>
                <span className="cl-label">{item.label}</span>
                <span className="cl-detail">{item.detail}</span>
              </span>
              <span className="own">
                <span className={`owdot ow-${item.role}`} />
                {roleShortLabels[item.role]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
